/**
 * SecureEmbed Access Control - User Management
 *
 * Enhanced user management with organization, dataset, and tier assignments
 * Admin-only access
 */

import React, { useEffect, useState, useCallback } from 'react';
import {
  Users, RefreshCw, Loader2, AlertCircle, CheckCircle,
  X, Search, Shield, ChevronRight
} from 'lucide-react';
import { orgApi, userAccessApi, userPoolApi } from '../services/api';

interface UserRecord {
  username: string;
  email?: string;
  status?: string;
  groups?: string[];
}

interface UserAccess {
  organization_id: string;
  datasets: string[];
  admin: boolean;
  analysis_tier: string;
  can_save_views: boolean;
  can_share_views: boolean;
}

interface OrgOption {
  id: string;
  name: string;
}

const DATASET_OPTIONS = [
  { value: 'aemo', label: 'AEMO Energy Market' },
  { value: 'aer-pricing', label: 'AER Pricing' },
  { value: 'bom', label: 'BOM Weather' },
];

const TIER_OPTIONS = [
  { value: 'tier1', label: 'Tier 1 - Search & Metadata' },
  { value: 'tier2', label: 'Tier 2 - Analytics & Queries' },
  { value: 'tier3', label: 'Tier 3 - Streaming & Webhooks' },
];

const AccessUsersPage: React.FC<{ embedded?: boolean }> = ({ embedded = false }) => {
  const [users, setUsers] = useState<UserRecord[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [message, setMessage] = useState('');
  const [searchTerm, setSearchTerm] = useState('');

  // Organizations for dropdown
  const [organizations, setOrganizations] = useState<OrgOption[]>([]);

  // Edit panel
  const [selectedUser, setSelectedUser] = useState<string | null>(null);
  const [userAccess, setUserAccess] = useState<UserAccess>({
    organization_id: '',
    datasets: [],
    admin: false,
    analysis_tier: 'tier1',
    can_save_views: false,
    can_share_views: false,
  });
  const [accessLoading, setAccessLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  // Fetch users list
  const fetchUsers = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      // Route through the shared service layer (centralised auth-token injection,
      // error handling and 401-logout) instead of a raw fetch that ignored
      // response.ok and could silently render an empty list on failure.
      const data = await userPoolApi.listUsers();
      if (data?.users) {
        setUsers(data.users);
      } else if (Array.isArray(data)) {
        setUsers(data);
      } else {
        setUsers([]);
      }
    } catch (e: any) {
      setError(e?.response?.data?.message || e?.message || 'Failed to load users');
      setUsers([]);
    } finally {
      setLoading(false);
    }
  }, []);

  // Fetch organizations for dropdown
  const fetchOrganizations = useCallback(async () => {
    try {
      const response = await orgApi.listOrganisations();
      const orgs = response?.organisations || [];
      setOrganizations(orgs.map((o: any) => ({ id: o.org_id, name: o.name })));
    } catch {
      // Organizations API may not be deployed yet
      setOrganizations([]);
    }
  }, []);

  useEffect(() => {
    fetchUsers();
    fetchOrganizations();
  }, [fetchUsers, fetchOrganizations]);

  // Load user access when selected
  const selectUser = async (username: string) => {
    setSelectedUser(username);
    setAccessLoading(true);
    setError(null);
    try {
      const response = await userAccessApi.get(username);
      if (response) {
        setUserAccess({
          organization_id: response.organization_id || '',
          datasets: response.datasets || [],
          admin: response.admin || false,
          analysis_tier: response.analysis_tier || 'tier1',
          can_save_views: response.can_save_views || false,
          can_share_views: response.can_share_views || false,
        });
      }
    } catch {
      // No access record yet - use defaults
      setUserAccess({
        organization_id: '',
        datasets: [],
        admin: false,
        analysis_tier: 'tier1',
        can_save_views: false,
        can_share_views: false,
      });
    } finally {
      setAccessLoading(false);
    }
  };

  const handleSave = async () => {
    if (!selectedUser) return;
    setSaving(true);
    setError(null);
    try {
      await userAccessApi.update(selectedUser, userAccess);
      setMessage('Access settings saved');
      setTimeout(() => setMessage(''), 3000);
    } catch (e: any) {
      setError(e.message || 'Failed to save access settings');
    } finally {
      setSaving(false);
    }
  };

  const toggleDataset = (value: string) => {
    setUserAccess(prev => ({
      ...prev,
      datasets: prev.datasets.includes(value)
        ? prev.datasets.filter(d => d !== value)
        : [...prev.datasets, value],
    }));
  };

  const filtered = users.filter(u =>
    (u.username || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
    (u.email || '').toLowerCase().includes(searchTerm.toLowerCase())
  );

  const selectedUserRecord = users.find(u => u.username === selectedUser);

  return (
    <div className="space-y-6">
      {/* Header — suppressed when embedded in the Users & Orgs hub (the hub
          renders the single canonical PageHeader; a plain refresh row remains). */}
      {!embedded ? (
        <div className="bg-gradient-to-r from-purple-600 to-purple-700 rounded-2xl p-6 text-white shadow-lg">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <div className="w-14 h-14 bg-white/20 rounded-xl flex items-center justify-center">
                <Users className="w-8 h-8" />
              </div>
              <div>
                <h1 className="text-2xl font-bold">User Access Control</h1>
                <p className="text-purple-200 mt-1">Manage user organizations, datasets, and permissions</p>
              </div>
            </div>
            <button
              onClick={fetchUsers}
              disabled={loading}
              className="p-2 bg-white/10 rounded-lg hover:bg-white/20 transition-colors"
            >
              <RefreshCw className={`w-5 h-5 ${loading ? 'animate-spin' : ''}`} />
            </button>
          </div>
        </div>
      ) : (
        <div className="flex justify-end">
          <button
            onClick={fetchUsers}
            disabled={loading}
            className="p-2 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-colors disabled:opacity-50"
          >
            <RefreshCw className={`w-5 h-5 ${loading ? 'animate-spin' : ''}`} />
          </button>
        </div>
      )}

      {/* Messages */}
      {error && (
        <div className="p-4 bg-red-50 border border-red-200 rounded-lg flex items-start gap-3">
          <AlertCircle className="w-5 h-5 text-red-500 mt-0.5 flex-shrink-0" />
          <p className="text-sm text-red-700">{error}</p>
        </div>
      )}
      {message && (
        <div className="p-4 bg-green-50 border border-green-200 rounded-lg flex items-center gap-3">
          <CheckCircle className="w-5 h-5 text-green-500" />
          <p className="text-sm text-green-700">{message}</p>
        </div>
      )}

      <div className="flex gap-6">
        {/* User List */}
        <div className={`bg-gray-100 rounded-xl shadow-sm border border-gray-100 overflow-hidden ${selectedUser ? 'w-1/2' : 'w-full'}`}>
          <div className="p-4 border-b border-gray-100 flex items-center justify-between">
            <h3 className="font-semibold text-gray-900">Users ({filtered.length})</h3>
            <div className="relative">
              <Search className="w-4 h-4 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2" />
              <input
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Search..."
                className="pl-9 pr-4 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500"
              />
            </div>
          </div>

          {loading ? (
            <div className="p-12 text-center">
              <Loader2 className="w-8 h-8 animate-spin text-purple-500 mx-auto mb-2" />
              <p className="text-gray-500">Loading users...</p>
            </div>
          ) : (
            <table className="w-full">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Username</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Email</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                  <th className="px-4 py-3 w-8"></th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {filtered.map((user) => (
                  <tr
                    key={user.username}
                    onClick={() => selectUser(user.username)}
                    className={`cursor-pointer transition-colors ${
                      selectedUser === user.username
                        ? 'bg-purple-50'
                        : 'hover:bg-gray-50'
                    }`}
                  >
                    <td className="px-4 py-3 font-medium text-gray-900 text-sm">{user.username}</td>
                    <td className="px-4 py-3 text-sm text-gray-600">{user.email || '-'}</td>
                    <td className="px-4 py-3">
                      <span className={`text-xs px-2 py-1 rounded-full ${
                        user.status === 'CONFIRMED' || user.status === 'enabled'
                          ? 'bg-green-100 text-green-700'
                          : 'bg-yellow-100 text-yellow-700'
                      }`}>
                        {user.status || 'unknown'}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <ChevronRight className="w-4 h-4 text-gray-400" />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        {/* Edit Panel */}
        {selectedUser && (
          <div className="w-1/2 bg-gray-100 rounded-xl shadow-sm border border-gray-100 overflow-hidden">
            <div className="p-4 border-b border-gray-100 flex items-center justify-between">
              <div>
                <h3 className="font-semibold text-gray-900 flex items-center gap-2">
                  <Shield className="w-4 h-4 text-purple-500" />
                  {selectedUser}
                </h3>
                {selectedUserRecord?.email && (
                  <p className="text-sm text-gray-500">{selectedUserRecord.email}</p>
                )}
              </div>
              <button
                onClick={() => setSelectedUser(null)}
                className="text-gray-400 hover:text-gray-600"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {accessLoading ? (
              <div className="p-12 text-center">
                <Loader2 className="w-6 h-6 animate-spin text-purple-500 mx-auto mb-2" />
                <p className="text-sm text-gray-500">Loading access settings...</p>
              </div>
            ) : (
              <div className="p-4 space-y-5">
                {/* Organization */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Organization</label>
                  <select
                    value={userAccess.organization_id}
                    onChange={(e) => setUserAccess(prev => ({ ...prev, organization_id: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-purple-500"
                  >
                    <option value="">-- No organization --</option>
                    {organizations.map(org => (
                      <option key={org.id} value={org.id}>{org.name}</option>
                    ))}
                  </select>
                </div>

                {/* Datasets */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Datasets</label>
                  <div className="space-y-2">
                    {DATASET_OPTIONS.map(opt => (
                      <label key={opt.value} className="flex items-center gap-2 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={userAccess.datasets.includes(opt.value)}
                          onChange={() => toggleDataset(opt.value)}
                          className="rounded border-gray-300 text-purple-600 focus:ring-purple-500"
                        />
                        <span className="text-sm text-gray-700">{opt.label}</span>
                      </label>
                    ))}
                  </div>
                </div>

                {/* Analysis Tier */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Analysis Tier</label>
                  <select
                    value={userAccess.analysis_tier}
                    onChange={(e) => setUserAccess(prev => ({ ...prev, analysis_tier: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-purple-500"
                  >
                    {TIER_OPTIONS.map(opt => (
                      <option key={opt.value} value={opt.value}>{opt.label}</option>
                    ))}
                  </select>
                </div>

                {/* Toggles */}
                <div className="space-y-3">
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-gray-700">Admin</span>
                    <label className="relative inline-flex items-center cursor-pointer">
                      <input
                        type="checkbox"
                        checked={userAccess.admin}
                        onChange={(e) => setUserAccess(prev => ({ ...prev, admin: e.target.checked }))}
                        className="sr-only peer"
                      />
                      <div className="w-11 h-6 bg-gray-200 peer-focus:ring-2 peer-focus:ring-purple-500 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-gray-100 after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-purple-600"></div>
                    </label>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-gray-700">Can Save Views</span>
                    <label className="relative inline-flex items-center cursor-pointer">
                      <input
                        type="checkbox"
                        checked={userAccess.can_save_views}
                        onChange={(e) => setUserAccess(prev => ({ ...prev, can_save_views: e.target.checked }))}
                        className="sr-only peer"
                      />
                      <div className="w-11 h-6 bg-gray-200 peer-focus:ring-2 peer-focus:ring-purple-500 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-gray-100 after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-purple-600"></div>
                    </label>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-gray-700">Can Share Views</span>
                    <label className="relative inline-flex items-center cursor-pointer">
                      <input
                        type="checkbox"
                        checked={userAccess.can_share_views}
                        onChange={(e) => setUserAccess(prev => ({ ...prev, can_share_views: e.target.checked }))}
                        className="sr-only peer"
                      />
                      <div className="w-11 h-6 bg-gray-200 peer-focus:ring-2 peer-focus:ring-purple-500 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-gray-100 after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-purple-600"></div>
                    </label>
                  </div>
                </div>

                {/* Save */}
                <div className="pt-4 border-t border-gray-100">
                  <button
                    onClick={handleSave}
                    disabled={saving}
                    className="w-full flex items-center justify-center gap-2 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:opacity-50 font-medium"
                  >
                    {saving && <Loader2 className="w-4 h-4 animate-spin" />}
                    {saving ? 'Saving...' : 'Save Access Settings'}
                  </button>
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default AccessUsersPage;
